import { BulkExecutionResult, BulkPreflightResult } from 'src/models/promotion/BulkPromotionTypes';
import { BulkPromotePhase } from 'src/models/promotion/bulkPromotionEnums';
import { NamedComponent } from 'src/components/calculations/bulk-promote/bulkNameResolver';
import { ResolvedIdentifier } from 'src/components/calculations/bulk-promote/bulkIdentifierClientParser';

/**
 * Phases do not form a strict total order: a batch that promotes in waves alternates between
 * EXECUTING_WAVE and AT_BARRIER once per wave, so those two share a rank. A message is accepted
 * when it advances a rank, or when it moves between two phases of the same rank; anything that
 * would move the flow backwards is a late delivery of an earlier phase and is ignored, as is a
 * phase that has no rank at all.
 */
export const PHASE_RANK: Record<string, number> = {
    [BulkPromotePhase.RESOLVING]: 0,
    [BulkPromotePhase.VALIDATING]: 1,
    [BulkPromotePhase.BLOCKED_LIST_READY]: 2,
    [BulkPromotePhase.EXECUTING_WAVE]: 3,
    [BulkPromotePhase.AT_BARRIER]: 3,
    [BulkPromotePhase.COMPLETE]: 4,
};

/** Reporting order, for anything that needs to present the phases as a sequence. */
export const PHASE_ORDER: string[] = [
    BulkPromotePhase.RESOLVING,
    BulkPromotePhase.VALIDATING,
    BulkPromotePhase.BLOCKED_LIST_READY,
    BulkPromotePhase.EXECUTING_WAVE,
    BulkPromotePhase.AT_BARRIER,
    BulkPromotePhase.COMPLETE,
];

export enum BulkPromoteStep {
    INPUT = 'INPUT',
    AWAITING_VERDICT = 'AWAITING_VERDICT',
    CONFIRMING = 'CONFIRMING',
    EXECUTING = 'EXECUTING',
    DONE = 'DONE',
}

/**
 * How long to wait for a verdict before saying it is overdue. The preflight machine is not quick at
 * 800 components and there are no progress pushes to interpolate with, so this is deliberately
 * generous: the point is to end an indefinite spinner, not to guess a deadline. Crossing it cancels
 * nothing — the batch is still running, and a verdict arriving later is still accepted.
 */
export const VERDICT_PATIENCE_MILLIS = 5 * 60 * 1000;

export interface BulkPromotionState {
    step: BulkPromoteStep;
    phase: string | null;
    batchId: string | null;
    acceptedCount: number;
    /**
     * Every batch a submit in this tab has replaced. A late frame for any of them is rejected rather than
     * adopted, which is what keeps a resubmit from taking an earlier batch's id.
     *
     * A list rather than one slot, because one slot could not hold the case it existed for. batchId is null
     * until a submit is acknowledged, so a third submit made before the second was acked wrote null over the
     * first batch's id and erased the guard — after which a late frame for that first batch passed every check
     * and was adopted as the third's verdict. The operator would then be shown one batch's blocked list and
     * prerequisites as another's, and confirming would consent to a promotion against a verdict computed for
     * a batch they never submitted.
     *
     * Appending is what makes that unreachable: an id that enters this list never leaves it for the lifetime
     * of the flow, so no later submit can displace it.
     */
    supersededBatchIds: string[];
    preflight: BulkPreflightResult | null;
    execution: BulkExecutionResult | null;
    barrierWaitMillis: number | null;
    errorMessage: string | null;
    /**
     * When the preflight submit went out, so the wait reads as elapsing rather than as a frozen
     * spinner. Null whenever nothing is outstanding.
     */
    awaitingVerdictSince: number | null;
    /**
     * Page numbers of the report frames already folded in. Frames are best-effort per page and can
     * arrive twice or not at all, so which were seen is tracked rather than only how many.
     */
    verdictPagesSeen: number[];
    /** Set when the verdict has not arrived within VERDICT_PATIENCE_MILLIS. Advisory only. */
    verdictOverdue: boolean;
    /**
     * Set when the connection dropped with a verdict or an outcome outstanding. The push cannot be
     * redelivered — the service posts to a connection id and this browser will have a new one — so
     * re-reading the batch is the only honest recovery.
     */
    pushLost: boolean;
    componentType: string | null;
    pastedIdentifiers: string;
    resolvedRows: ResolvedIdentifier[];
    /**
     * Every component of the selected type as (id, name, status), for resolving a pasted NAME to an id.
     * Empty for CLI_CONFIG and PLI_CONFIG, whose ids are already the readable identifier, and empty until
     * the fetch lands — a paste made before then resolves ids normally and names report as not found.
     */
    namedComponents: NamedComponent[];
    acceptedIds: string[];
    overCapBy: number;
    loading: boolean;
}

export const initialState: BulkPromotionState = {
    step: BulkPromoteStep.INPUT,
    phase: null,
    batchId: null,
    acceptedCount: 0,
    supersededBatchIds: [],
    preflight: null,
    execution: null,
    barrierWaitMillis: null,
    errorMessage: null,
    awaitingVerdictSince: null,
    verdictPagesSeen: [],
    verdictOverdue: false,
    pushLost: false,
    componentType: null,
    pastedIdentifiers: '',
    resolvedRows: [],
    namedComponents: [],
    acceptedIds: [],
    overCapBy: 0,
    loading: false,
};
